/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { Ticket } from '@touchplan/common';
import { TicketService } from '../services/ticket/ticket.service';
import { SideNavService } from '../services/side-nav/side-nav.service';
import { AppStateUser } from 'src/app/services/app-state.service';
import { cssVar } from '@touchplan/css-var';
import { filter, map, takeUntil } from 'rxjs/operators';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { getTicketFieldsStartDate, getTicketFieldsEndDate } from 'src/app/utils/array-util';
import { PLAN_ACCESS_ON_LOAD, PAGE_TITLE_PLAN_TICKETS } from '@touchplan/strings';
import { BannerMessageHandler } from 'src/app/common/logging/banner-message-handler';
import { OrganizationDisclaimerService } from 'src/app/services/organization-disclaimer.service';

@Component({
	selector: 'ticket-list',
	templateUrl: './ticket-list.component.html',
	styleUrls: ['./ticket-list.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [OrganizationDisclaimerService],
})
export class TicketListComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild(CdkVirtualScrollViewport)
	public viewport: CdkVirtualScrollViewport;

	public numOfPlanTicket$: Observable<number>;
	public numOfFilteredTicket$: Observable<number>;

	public planId: string;
	public projectId: string;

	private virtualSortedTicketsSync: Ticket[];
	private sortBySync: string;

	private shutdownSubject$: Subject<boolean> = new Subject();
	public ticketListHeight = cssVar.ticketListHeight_px;

	/*
		In the HTML, "minBufferPx" is the minimum amount of content buffer (in pixels) that the viewport must render.
		The following value is a ticket count, and it's multiplied by the height of each item in the ticket list to
		calculate the "minBufferPx".
		If the viewport ever detects that there is less buffered content it will immediately render more.
		Note that "render" in this context refers to the "rendered range", which is not the size of the visible
		sub-window in which the scrolled data is displayed.  The "rendered range" includes that data PLUS additional
		pixels/data before and additional data after the displayed data.  So the "rendered range" can also be thought
		of as the "prefetched range" or the "buffered range".
	*/
	public ticketsPerMinBufferHeight = 20;
	/*
		In the HTML, "maxBufferPx" is how much buffer space to render back up to when it detects that more buffer is required.
		The following value is a ticket count, and it's multiplied by the height of each item in the ticket list to
		calculate the "maxBufferPx".
	*/
	public ticketsPerMaxBufferHeight = 30;

	private userData: AppStateUser;

	constructor(
		public router: Router,
		private route: ActivatedRoute,
		public ticketService: TicketService,
		private sideNavService: SideNavService,
		private changeDetectorRef: ChangeDetectorRef,
		private bannerMessageHandler: BannerMessageHandler,
		private organizationDisclaimerService: OrganizationDisclaimerService
	) {}

	public ngOnInit(): void {
		this.sideNavService.setDisplayBackArrowIcon(true);
		this.sideNavService.setPageTitle(PAGE_TITLE_PLAN_TICKETS);
		this.sideNavService.setDisplayScrollToTodayIcon(true);
		this.sideNavService.setDisplayFilterIcon(true);
		this.ticketService.plan$
			.pipe(
				takeUntil(this.shutdownSubject$),
				filter((plan) => !!plan)
			)
			.subscribe((plan) => {
				if (plan.view.name) {
					this.sideNavService.setPageTitle(plan.view.name);
				}
			});

		this.route.params?.pipe(takeUntil(this.shutdownSubject$)).subscribe((params) => {
			this.planId = params.planId;
			if (!this.projectId || params.projectId !== this.projectId) {
				this.userData = this.route.snapshot.data.userData;

				//this check currently is basically a stand in for a ticket.service lazy load, should probably switch that out
				if (!this.ticketService.loaded) {
					this.ticketService.setupData(params.projectId, params.planId);
				}

				const projectMemberId = this.userData.indices.get(params.projectId)?.projectMemberId;
				if (projectMemberId) {
					this.ticketService.setupCustomFieldsApi(params.projectId, params.planId, projectMemberId);
				}
			}
			this.projectId = params.projectId;
			this.checkForPlanAccessOnLoad();
		});

		this.ticketService.filteredTicketList$
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((virtualSortedTickets) => (this.virtualSortedTicketsSync = virtualSortedTickets));

		this.ticketService.sortBy$
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((sortBy) => (this.sortBySync = sortBy));

		this.numOfPlanTicket$ = this.ticketService.ticketList$.pipe(
			map((tickets) => {
				const ticketArray = tickets as Ticket[];
				return ticketArray ? ticketArray.length : 0;
			}),
			takeUntil(this.shutdownSubject$)
		);
		this.numOfFilteredTicket$ = this.ticketService.filteredTicketList$.pipe(
			map((ticketArray) => (ticketArray ? ticketArray.length : 0)),
			takeUntil(this.shutdownSubject$)
		);
		this.organizationDisclaimerService.subscribeAndShowOrganizationDisclaimer(
			this.projectId,
			this.userData.$id,
			this.planId
		);
	}

	// Anything that uses "this.viewport" needs to wait until this function is called
	public ngAfterViewInit(): void {
		this.viewport?.renderedRangeStream.subscribe((newRange) =>
			this.handleRenderedRangeChange(newRange.start, newRange.end)
		);

		/*
		this.ticketService.virtualListManager.totalNumberOfTicket$.pipe(
			debounceTime(50),
			takeUntil(this.shutdownSubject$)
		)
		.subscribe(ticketCount => {
			this.viewport.setTotalContentSize(ticketCount * this.ticketListHeight);
		});
		*/

		/*
			Scroll when the list is first initialized
			MAYBE LATER

		let firstTime = true;
		this.ticketService.filteredTicketList$.pipe(
			debounceTime(50),
			takeUntil(this.shutdownSubject$)
		)
		.subscribe(ticketCount => {
			if (firstTime && this.virtualSortedTicketsSync) {
				this.scrollToToday();
				firstTime = false;
			}
		});
		*/

		// When the "Scroll to Today" button is clicked, this scrolls to the item closest to today:
		this.sideNavService.scrollToToday$
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe(() => this.scrollToToday());
	}

	private handleRenderedRangeChange(start: number, end: number): void {
		this.ticketService.handleRenderedRangeChange(start, end);
	}

	private scrollToToday() {
		if (this.virtualSortedTicketsSync) {
			const todayOrAfterTicketIndex = this.getTodayOrAfterTicketIndex(this.virtualSortedTicketsSync);
			if (todayOrAfterTicketIndex >= 0) {
				this.viewport.scrollToIndex(todayOrAfterTicketIndex);
			}
		}
	}

	private getTodayOrAfterTicketIndex(tickets: Ticket[]) {
		/*
			TODO:
			This could be made more efficient.  This is saved from a comment added to a PR by Kurtis.

			"...  it’s typically easier to break out dayjs and do a day diff.  It is notable though that date math is a surprisingly
			demanding operation and it might be worth breaking out the dateCache service, especially if this is called relatively frequently."
			(Kurtis)
		*/
		return tickets.findIndex((ticket) => {
			const ticketDateString =
				this.sortBySync === 'start_date'
					? getTicketFieldsStartDate(ticket.view)
					: getTicketFieldsEndDate(ticket.view);
			const ticketDate = new Date(ticketDateString);
			ticketDate.setHours(0, 0, 0, 0);
			// The ticket's date is ACTUALLY a day less than you'd expect, so...
			ticketDate.setDate(ticketDate.getDate() + 1);

			const today = new Date();
			today.setHours(0, 0, 0, 0);

			return ticketDate.getTime() - today.getTime() >= 0;
		});
	}

	public selectTicket(ticket: Ticket) {
		this.ticketService.selectTicket(ticket.$id);
	}

	public ngOnDestroy(): void {
		this.shutdownSubject$.next(true);
		this.ticketService.boom();
	}

	private checkForPlanAccessOnLoad() {
		this.ticketService
			.allowPlanAccessOnLoad$()
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((allowAccess) => {
				if (!allowAccess) {
					this.bannerMessageHandler.displayErrorMessage(new Error(PLAN_ACCESS_ON_LOAD));
					this.router.navigate(['/projects', this.projectId]);
				}
			});
	}
}
